// src/api.js (Create React App / react-scripts)

// 1) Prefer CRA standard env var name(s), keep your existing one too.
//    - Use REACT_APP_API_URL (common) OR your REACT_APP_API_BASE
//    - Default to local dev
export const API_BASE = (
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE ||
  "http://127.0.0.1:8001"
).replace(/\/$/, "");

// 2) Simple fetch with timeout (helps with Render cold starts / hangs)
function fetchWithTimeout(url, options = {}, timeoutMs = 30000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timer));
}

// 3) Build headers safely and set Accept: application/json by default.
//    (Helps FastAPI + consistent parsing)
function buildHeaders(extra = {}) {
  return {
    Accept: "application/json",
    ...extra,
  };
}

async function request(path, options = {}, timeoutMs = 30000) {
  const url = `${API_BASE}${path}`;
  let res;

  // 4) Add a tiny automatic retry for Render cold starts / transient 502/503/504
  //    Only retries GET by default (safe). You can expand later if needed.
  const method = (options.method || "GET").toUpperCase();
  const shouldRetry =
    method === "GET"; // keep conservative; avoids accidental double-POST

  const attempt = async () => {
    return fetchWithTimeout(
      url,
      {
        ...options,
        headers: buildHeaders(options.headers || {}),
      },
      timeoutMs
    );
  };

  try {
    res = await attempt();

    // One retry if Render is waking up or gateway times out
    if (shouldRetry && [502, 503, 504].includes(res.status)) {
      // small backoff
      await new Promise((r) => setTimeout(r, 800));
      res = await attempt();
    }
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error(`Request timed out after ${timeoutMs / 1000}s: ${url}`);
    }
    throw new Error(`Network error while calling ${url}: ${err?.message || err}`);
  }

  // Read body safely as text first, then try JSON parse
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text; // keep raw text if not JSON
  }

  if (!res.ok) {
    // FastAPI errors often look like { detail: ... }
    let detail = "";

    if (data && typeof data === "object") {
      if (data.detail) {
        detail = typeof data.detail === "string"
          ? data.detail
          : JSON.stringify(data.detail);
      } else {
        detail = JSON.stringify(data);
      }
    } else if (typeof data === "string") {
      detail = data;
    }

    const suffix = detail ? ` — ${detail}` : "";
    throw new Error(`${res.status} ${res.statusText}${suffix}`);
  }

  return data;
}

/* --------------------------------------
   OPTIONAL: Quick health check helper
   (Useful for debugging CORS / domain wiring)
--------------------------------------- */
export function health() {
  return request("/health");
}

/* --------------------------------------
   FETCH ALL SAVED HUNTS (legacy)
--------------------------------------- */
export function listHunts() {
  return request("/api/hunts");
}

/* --------------------------------------
   CREATE A NEW HUNT (legacy)
--------------------------------------- */
export function createHunt(payload) {
  return request("/api/hunts", {
    method: "POST",
    headers: buildHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
}

/* --------------------------------------
   GENERATE MATH TASKS (legacy)
   Returns { tasks: [] } when backend returns 404
--------------------------------------- */
export async function generateTasks({
  age,
  difficulty,
  subject = "math",
  numTasks = 5,
}) {
  try {
    return await request(
      "/api/generate",
      {
        method: "POST",
        headers: buildHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          age,
          difficulty,
          subject,
          num_tasks: numTasks,
        }),
      },
      30000
    );
  } catch (err) {
    if (String(err?.message || "").startsWith("404 ")) {
      return { tasks: [] };
    }
    throw err;
  }
}

/* --------------------------------------
   CREATE NUMBER SCAVENGER HUNT
--------------------------------------- */
export function createNumberHunt(config) {
  return request(
    "/api/number-hunt",
    {
      method: "POST",
      headers: buildHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(config),
    },
    45000
  );
}
